import { DotFilledIcon } from '@radix-ui/react-icons';
import { Box, Flex, Heading, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HPSummarySectionCard } from 'components/sections/hitter-library/profile/summary/sections/section-card';
import useHitterProfileStore from 'components/sections/hitter-library/profile/use-hitter-profile-store';
import { t } from 'i18next';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useMemo, useState } from 'react';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Props as LegendProps } from 'recharts/types/component/Legend';

enum Contact {
  Hard = 'Hard',
  Medium = 'Medium',
  Weak = 'Weak',
}

const HARD_CONTACT_THRESHOLD_MPH = 95;
const MEDIUM_CONTACT_THRESHOLD_MPH = 80;
const OUTER_RADIUS_PX = 96;
const INNER_RADIUS_PX = 60;

const getContactColor = (contact: Contact): string => {
  switch (contact) {
    case Contact.Hard:
      return 'var(--accent-a9)';
    case Contact.Medium:
      return 'var(--accent-a7)';
    case Contact.Weak:
    default:
      return 'var(--accent-a5)';
  }
};

const getLegendLabel = (contact: Contact): string => {
  switch (contact) {
    case Contact.Hard:
      return t('hitters.hard-contact-speed-x', {
        x: `${HARD_CONTACT_THRESHOLD_MPH}+`,
      });
    case Contact.Medium:
      return t('hitters.mid-contact-speed-x', {
        x: `${MEDIUM_CONTACT_THRESHOLD_MPH}-${HARD_CONTACT_THRESHOLD_MPH}`,
      });
    case Contact.Weak:
    default:
      return t('hitters.weak-contact-speed-x', {
        x: `<${MEDIUM_CONTACT_THRESHOLD_MPH}`,
      });
  }
};

interface IHitQualityLegendProps extends LegendProps {
  onHover: (index: number) => void;
}

const HitQualityLegend = (props: IHitQualityLegendProps) => {
  const { payload, onHover } = props;

  if (!payload) {
    return null;
  }

  return (
    <Flex
      direction="column"
      gap={RADIX.FLEX.GAP.XS}
      position="absolute"
      bottom="0"
      // move legend below chart on smaller devices
      mb={{
        xs: payload.length > 2 ? '-8' : '-6',
        lg: '0',
      }}
    >
      {payload.map((entry, i) => (
        <Flex
          gap={RADIX.FLEX.GAP.XS}
          key={`legend-item-${entry.value}`}
          className="cursor-pointer"
          onMouseEnter={() => onHover(i)}
          align="center"
        >
          <DotFilledIcon
            color={getContactColor(entry.value)}
            height="20"
            width="20"
          />
          <Text color={RADIX.COLOR.SECONDARY}>
            {getLegendLabel(entry.value)}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

const getActiveSegmentPercent = (
  segmentTotal: number,
  overallTotal: number
): string => {
  if (overallTotal === 0) {
    return '0';
  }

  const percent = (segmentTotal / overallTotal) * 100;

  if (percent < 1) {
    return percent.toFixed(1);
  }

  return percent.toFixed(0);
};

export const HPSummaryHitQuality = () => {
  const currSummaryHits = useHitterProfileStore((s) => s.currSummaryHits);
  const hitsFilter = useHitterProfileStore((s) => s.hitsFilter);
  const loading = useHitterProfileStore((s) => s.loading);

  const validCurrHits = useMemo(
    () => currSummaryHits.filter((h) => !h.avg_excluded),
    [currSummaryHits]
  );

  const data = useMemo((): {
    rating: Contact;
    total: number;
  }[] => {
    const asDict = ArrayHelper.groupByFn(validCurrHits, (h) => {
      const safeExitMPH = h.hit?.exitMPH ?? 0;

      if (safeExitMPH >= 95) {
        return Contact.Hard;
      }

      if (safeExitMPH >= 80) {
        return Contact.Medium;
      }

      return Contact.Weak;
    });

    const output = Object.keys(asDict)
      .sort()
      .map((key) => ({
        total: asDict[key]?.length ?? 0,
        rating: key as Contact,
      }));

    return output;
  }, [validCurrHits, hitsFilter.period]);

  const [activeIndex, setActiveIndex] = useState(0);

  const activeSegment = data[activeIndex];
  const activeSegmentPercent =
    activeSegment &&
    getActiveSegmentPercent(activeSegment.total, validCurrHits.length);

  return (
    <ErrorBoundary identifier="HitterProfileSummaryHitQuality">
      <HPSummarySectionCard loading={loading}>
        <Flex direction="column">
          <Box>
            <Flex justify="between" align="center">
              <Heading size="3">Hit Quality Profile</Heading>
            </Flex>
            <Text color={RADIX.COLOR.NEUTRAL}>
              Showing the percentage of batted balls based on exit velocity
            </Text>
          </Box>
          <Box
            // add bottom space to move legend on smaller devices
            pb={{
              xs: data.length > 2 ? '8' : '5',
              lg: '0',
            }}
          >
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  cx="50%"
                  cy="50%"
                  data={data}
                  dataKey="total"
                  innerRadius={INNER_RADIUS_PX}
                  outerRadius={OUTER_RADIUS_PX}
                  stroke="transparent"
                  nameKey="rating"
                  onMouseEnter={(_, i) => setActiveIndex(i)}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${entry.rating}`}
                      fill={getContactColor(entry.rating)}
                      className="cursor-pointer"
                      stroke={
                        index === activeIndex
                          ? 'var(--gray-a11)'
                          : 'transparent'
                      }
                    />
                  ))}
                </Pie>
                <Tooltip
                  cursor={{ fill: 'var(--accent-9)', fillOpacity: 0.08 }}
                  contentStyle={{
                    backgroundColor: 'var(--gray-6)',
                    border: '1px solid var(--gray-7)',
                    color: 'var(--gray-12)',
                  }}
                  content={({ payload }) => {
                    const numPitches = payload?.[0] ? payload[0].value : 0;
                    const contact = payload?.[0]?.name as Contact;

                    if (!contact || !numPitches) {
                      return null;
                    }

                    return (
                      <Flex
                        style={{
                          backgroundColor: 'var(--gray-6)',
                          border: '1px solid var(--gray-7)',
                          padding: '8px 12px',
                        }}
                        direction="column"
                        gap={RADIX.FLEX.GAP.XS}
                      >
                        <Text mb="1" color={RADIX.COLOR.NEUTRAL} weight="bold">
                          {getLegendLabel(contact)}
                        </Text>
                        <Text color={RADIX.COLOR.NEUTRAL}>
                          {numPitches} /{' '}
                          {t('hitters.x-batted-balls', {
                            x: validCurrHits.length,
                          })}
                        </Text>
                      </Flex>
                    );
                  }}
                />
                {activeSegment && (
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="text-after-edge"
                  >
                    <tspan
                      x="50%"
                      dy="7"
                      textAnchor="middle"
                      fill="var(--gray-12)"
                      fontSize={30}
                      fontWeight="700"
                    >
                      {activeSegmentPercent}%
                    </tspan>
                    <tspan
                      x="50%"
                      dy="16"
                      textAnchor="middle"
                      fill="var(--gray-a11)"
                      fontSize={14}
                      fontWeight="500"
                    >
                      {activeSegment.rating}
                    </tspan>
                  </text>
                )}
                <Legend
                  content={<HitQualityLegend onHover={setActiveIndex} />}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Flex>
      </HPSummarySectionCard>
    </ErrorBoundary>
  );
};
